import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTheme } from "@mui/material/styles";
import { Box, IconButton } from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import Horizon from "./components/Horizon";
import { Intersections, Celestials } from "./types";
import ZodiacCircle from "./components/ZodiacCircle";
import SeasonCircle from "./components/SeasonCircle";
import MonthCircle from "./components/MonthCircle";
import DayCircle from "./components/DayCircle";
import CelestialCircle from "./components/CelestialCircle";
import SunMoon from "./components/SunMoon";
import ZodiacRing from "./components/ZodiacRing";
import LocationDateTimeDialog from "./components/LocationDateTimeDialog";
import astroServiceAdapter from "./service/AstroServiceAdapter";

// Minimum sky movement (degrees) worth animating. The periodic auto-refresh
// only nudges the horizon by a fraction of a degree, so a full 4.5 s, 60 fps
// interpolation would keep the CPU busy for almost nothing. Anything below
// this threshold snaps straight to the new state (and keeps the stars
// twinkling, since no rotation animation runs).
const MIN_ANIMATION_DELTA = 1.5;

interface Props {
  rCelestials: number;
  rDays: number;
  rMonths: number;
  rSeasons: number;
  rZodiac: number;
  rEnd: number;
  rZodiacRingStart: number;
  rZodiacRingEnd: number;
  canvas: number[];
}

/** Shortest signed angular step from `from` to `to`, in [-180, 180]. */
const shortestDelta = (from: number, to: number): number => {
  let d = to - from;
  if (d > 180) d -= 360;
  else if (d < -180) d += 360;
  return d;
};

/**
 * Largest angular distance any part of the celestial state would travel
 * between two states: the horizon plus every body's *screen* position
 * (lon − west).
 */
const maxAngularDelta = (
  srcPos: Celestials,
  dstPos: Celestials,
  srcInt: Intersections,
  dstInt: Intersections,
): number => {
  let max = 0;
  const consider = (delta: number) => {
    const abs = Math.abs(delta);
    if (abs > max) max = abs;
  };

  consider(shortestDelta(srcInt.east, dstInt.east));
  consider(shortestDelta(srcInt.west, dstInt.west));
  for (const name of Object.keys(dstPos)) {
    const srcLon = srcPos[name]?.ecliptic?.lon ?? 0;
    const dstLon = dstPos[name]?.ecliptic?.lon ?? 0;
    consider(shortestDelta(srcLon - srcInt.west, dstLon - dstInt.west));
  }
  return max;
};

export const Astrolabe: React.FC<Props> = ({
  rCelestials,
  rDays,
  rMonths,
  rSeasons,
  rZodiac,
  rEnd,
  rZodiacRingStart,
  rZodiacRingEnd,
  canvas,
}) => {
  const theme = useTheme();
  const bg = theme.palette.background.default;
  const fg = theme.palette.text.primary;

  const [positions, setPositions] = useState<Celestials>({
    sun: { ecliptic: { lat: 0, lon: 0 } },
    jupiter: { ecliptic: { lat: 0, lon: 0 } },
    mars: { ecliptic: { lat: 0, lon: 0 } },
    mercury: { ecliptic: { lat: 0, lon: 0 } },
    moon: { ecliptic: { lat: 0, lon: 0 } },
    neptune: { ecliptic: { lat: 0, lon: 0 } },
    pluto: { ecliptic: { lat: 0, lon: 0 } },
    saturn: { ecliptic: { lat: 0, lon: 0 } },
    uranus: { ecliptic: { lat: 0, lon: 0 } },
    venus: { ecliptic: { lat: 0, lon: 0 } },
  });
  const [intersections, setIntersections] = useState<Intersections>({
    east: 180.0,
    west: 0.0,
  });
  const [dateTime, setDateTime] = useState<Date>(new Date());
  const [location, setLocation] = useState({
    lat: 47.36667,
    lon: 8.55,
    height: 0,
    name: "Zurich, Switzerland",
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  // Which tab the picker opens on (0 Date | 1 Time | 2 Location) — set by
  // tapping the matching line in the centre display.
  const [dialogTab, setDialogTab] = useState(0);
  // True once the user has picked a date/time/location: the periodic live
  // refresh pauses so their choice sticks, until they go back to live.
  const [isManual, setIsManual] = useState(false);

  // True while the celestial rotation animation runs: the stars stop
  // twinkling (see Horizon) so the expensive CSS animations don't fight the
  // rotating group on lower-power hardware.
  const [isAnimating, setIsAnimating] = useState(false);

  // Interpolated display values — animated smoothly from old to new.
  // A ref holds the latest values so the animation effect always reads
  // the true current state regardless of closure timing.
  const [displayPositions, setDisplayPositions] =
    useState<Celestials>(positions);
  const [displayIntersections, setDisplayIntersections] =
    useState<Intersections>(intersections);
  const displayRef = useRef({ positions, intersections });
  displayRef.current = {
    positions: displayPositions,
    intersections: displayIntersections,
  };
  const animRef = useRef<number>(0);

  // Animate from current display values to new source values.
  // displayPositions/displayIntersections are intentionally excluded
  // from deps — they are captured as animation source, not triggers.
  useEffect(() => {
    const srcPos = displayRef.current.positions;
    const srcInt = displayRef.current.intersections;
    const dstPos = positions;
    const dstInt = intersections;

    // Tiny movements (the periodic auto-refresh) snap instead of animating:
    // no rotation animation means no reason to stop the twinkling either.
    if (maxAngularDelta(srcPos, dstPos, srcInt, dstInt) < MIN_ANIMATION_DELTA) {
      setDisplayPositions(dstPos);
      setDisplayIntersections(dstInt);
      setIsAnimating(false);
      return;
    }

    setIsAnimating(true);

    const start = performance.now();
    const duration = 4500;

    const dWest = shortestDelta(srcInt.west, dstInt.west);
    const dEast = shortestDelta(srcInt.east, dstInt.east);

    const animate = (now: number) => {
      const raw = Math.min((now - start) / duration, 1);
      const t =
        raw < 0.5 ? 4 * raw * raw * raw : 1 - Math.pow(-2 * raw + 2, 3) / 2;

      const west = srcInt.west + dWest * t;

      const ip: Celestials = {};
      for (const name of Object.keys(dstPos)) {
        const srcLon = srcPos[name]?.ecliptic?.lon ?? 0;
        const dstLon = dstPos[name]?.ecliptic?.lon ?? 0;
        const srcLat = srcPos[name]?.ecliptic?.lat ?? 0;
        const dstLat = dstPos[name]?.ecliptic?.lat ?? 0;
        // Interpolate the *screen* position (lon − west) along the shortest
        // arc, then recompose with the interpolated west, so no body appears
        // to fly more than 180°.
        const dComposite = shortestDelta(
          srcLon - srcInt.west,
          dstLon - dstInt.west,
        );
        ip[name] = {
          ecliptic: {
            lat: srcLat + (dstLat - srcLat) * t,
            lon:
              ((((srcLon - srcInt.west + dComposite * t + west) % 360) + 360) %
                360),
          },
        };
      }

      setDisplayPositions(ip);
      setDisplayIntersections({ east: srcInt.east + dEast * t, west });

      if (raw < 1) {
        animRef.current = requestAnimationFrame(animate);
      } else {
        setIsAnimating(false);
      }
    };

    animRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positions, intersections]);

  // Fetch astrolabe data for the selected datetime and location
  const fetchAstrolabeData = useCallback(
    async (lat?: number, lon?: number, height?: number, date?: Date) => {
      try {
        const useDate = date ?? new Date();
        const dateString = useDate.toISOString();
        const useLat = lat ?? location.lat;
        const useLon = lon ?? location.lon;
        const useHeight = height ?? location.height;

        const data =
          await astroServiceAdapter.fetchEclipticPositionsForLocation(
            dateString,
            useLat,
            useLon,
            useHeight,
          );

        setPositions(data.positions);
        setIntersections(data.intersections);
        setDateTime(useDate);
      } catch (err) {
        console.error("Astrolabe: Error fetching positions:", err);
        // If the fetch never delivers new positions there is no animation
        // running to hide the twinkling for, so bring it back.
        setIsAnimating(false);
      }
    },
    [location.lat, location.lon, location.height],
  );

  // Keep a ref so the interval always sees the latest manual-mode flag without
  // restarting the polling effect (which would also re-run the initial fetch).
  const isManualRef = useRef(isManual);
  isManualRef.current = isManual;

  // Initial fetch — deferred by one animation frame so the browser
  // paints the zero-state render before the animation effect fires.
  // The periodic refresh is skipped while the tab is hidden (a background
  // tab would otherwise keep polling and re-running the animation), is
  // performed on return if the data has gone stale, and is paused entirely
  // while the user is browsing a manually picked date.
  useEffect(() => {
    const intervalMs = 5 * 60 * 1000;
    let lastFetch = 0;

    const refresh = () => {
      if (isManualRef.current) return;
      lastFetch = Date.now();
      fetchAstrolabeData();
    };

    const rafId = requestAnimationFrame(refresh);

    const intervalId = setInterval(() => {
      if (document.hidden) return;
      refresh();
    }, intervalMs);

    const onVisibilityChange = () => {
      if (
        !document.hidden &&
        !isManualRef.current &&
        Date.now() - lastFetch >= intervalMs
      ) {
        refresh();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      cancelAnimationFrame(rafId);
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [fetchAstrolabeData]);

  const handleDialogSubmit = (
    newDateTime: Date,
    lat: number,
    lon: number,
    locName: string,
  ) => {
    setLocation({ lat, lon, height: 0, name: locName });
    setDateTime(newDateTime);
    setIsManual(true);
    // The transition begins now (the fetch only drives the animation a beat
    // later) — stop the twinkling before the sky starts rotating.
    setIsAnimating(true);
    // Pass new values directly to avoid stale closure
    fetchAstrolabeData(lat, lon, 0, newDateTime);
  };

  // Tap the date / time / location line in the centre display to open the
  // picker on the matching tab.
  const handleInfoClick = useCallback((tab: number) => {
    setDialogTab(tab);
    setDialogOpen(true);
  }, []);

  // Return to the live sky at the currently selected location.
  const handleResetToLive = () => {
    const now = new Date();
    setIsManual(false);
    setIsAnimating(true);
    setDateTime(now);
    fetchAstrolabeData(location.lat, location.lon, location.height, now);
  };

  return (
    <React.Fragment>
      <Box
        sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            width: "100%",
            position: "relative",
          }}
        >
          {isManual && (
            <IconButton
              onClick={handleResetToLive}
              title="Back to live sky"
              aria-label="Back to live sky"
              sx={{
                position: "absolute",
                bottom: 16,
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 10,
                bgcolor: "warning.main",
                "&:hover": { bgcolor: "warning.dark" },
              }}
            >
              <LockIcon sx={{ color: "warning.contrastText" }} />
            </IconButton>
          )}
          <svg
            data-testid="astrolabe-svg"
            xmlns="http://www.w3.org/2000/svg"
            viewBox={`0 0 ${canvas[0]} ${canvas[1]}`}
            shapeRendering="geometricPrecision"
            style={{ width: "100%", height: "100%", display: "block" }}
          >
            <Horizon
              rCelestials={rCelestials}
              rDays={rDays}
              rEnd={rEnd}
              east={displayIntersections.east}
              west={displayIntersections.west}
              sun={displayPositions.sun.ecliptic.lon}
              moon={displayPositions.moon.ecliptic.lon}
              canvas={canvas}
              disableTwinkle={isAnimating}
            />
            <g
              data-testid="astrolabe-rotate-group"
              style={{
                transform: `rotate(${-displayIntersections.west - 180}deg)`,
                transformOrigin: `${canvas[0] / 2}px ${canvas[1] / 2}px`,
                willChange: "transform",
                contain: "layout style",
                backfaceVisibility: "hidden",
              }}
            >
              <g>
                <circle
                  key={`end-circle`}
                  fill="none"
                  strokeWidth="0.5"
                  stroke={fg}
                  cx={canvas[0] / 2}
                  cy={canvas[1] / 2}
                  r={rEnd}
                />
              </g>
              <CelestialCircle
                r={rCelestials}
                celestials={displayPositions}
                west={displayIntersections.west}
                fg={fg}
                bg={bg}
                canvas={canvas}
                dateTime={dateTime}
                locationName={location.name}
                onInfoClick={handleInfoClick}
              />
              <DayCircle
                rStart={rDays}
                rEnd={rMonths}
                fg={fg}
                bg={bg}
                canvas={canvas}
              />
              <MonthCircle
                rStart={rMonths}
                rEnd={rSeasons}
                fg={fg}
                bg={bg}
                canvas={canvas}
              />
              <SeasonCircle
                rStart={rSeasons}
                rEnd={rZodiac}
                fg={fg}
                bg={bg}
                canvas={canvas}
              />
              <ZodiacCircle
                rStart={rZodiac}
                rEnd={rEnd}
                canvas={canvas}
                fg={fg}
                bg={bg}
              />
              <ZodiacRing
                rStart={rZodiacRingStart}
                rEnd={rZodiacRingEnd}
                canvas={canvas}
                fg={fg}
                bg={bg}
                celestials={displayPositions}
              />
            </g>
            <SunMoon
              sunLon={displayPositions.sun.ecliptic.lon}
              moonLon={displayPositions.moon.ecliptic.lon}
              sunLat={displayPositions.sun.ecliptic.lat}
              moonLat={displayPositions.moon.ecliptic.lat}
              west={displayIntersections.west}
              east={displayIntersections.east}
              rCelestials={rCelestials}
              canvas={canvas}
            />
          </svg>
        </Box>
        <LocationDateTimeDialog
          open={dialogOpen}
          dateTime={dateTime}
          locationName={location.name}
          lat={location.lat}
          lon={location.lon}
          initialTab={dialogTab}
          onClose={() => setDialogOpen(false)}
          onSubmit={handleDialogSubmit}
        />
      </Box>
    </React.Fragment>
  );
};
